import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CostPeriod } from '@/versions/v1/modules/cost/Scene.transform';
import { ebitdaWaterfall, type EbitdaWaterfallBar } from './ebitda-bridge.derive';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

/** Plot height (bars and value labels) above the axis labels. */
const PLOT_H = 112;
const PAD = { l: 8, r: 8, t: 24 };
/** Axis label band: name + one sub-label line, or name + two when a sub wraps. */
const AXIS_ONE = 36;
const AXIS_TWO = 48;
const SUB_FONT_PX = 10;
/** Adjacent sub-labels keep at least this much air between them. */
const MIN_SUB_GAP = 10;

let measureCtx: CanvasRenderingContext2D | null | undefined;
function subWidth(text: string, family: string): number {
  if (measureCtx === undefined) measureCtx = document.createElement('canvas').getContext('2d');
  if (!measureCtx) return text.length * SUB_FONT_PX * 0.56;
  measureCtx.font = `${SUB_FONT_PX}px ${family}`;
  return measureCtx.measureText(text).width;
}

/** Two lines for a sub-label: after the ` · ` separator nearest the middle (the
 * dot ends the first line), after a hyphen, else at the space nearest the middle. */
function splitSub(text: string): string[] {
  const seps: number[] = [];
  for (let i = text.indexOf(' · '); i >= 0; i = text.indexOf(' · ', i + 1)) seps.push(i);
  if (seps.length > 0) {
    const mid = text.length / 2;
    const at = seps.reduce((best, i) => (Math.abs(i + 1 - mid) < Math.abs(best + 1 - mid) ? i : best));
    return [text.slice(0, at + 2), text.slice(at + 3)];
  }
  const hyphen = text.indexOf('-', 1);
  if (hyphen > 0 && hyphen < text.length - 1) return [text.slice(0, hyphen + 1), text.slice(hyphen + 1)];
  const mid = text.length / 2;
  let best = -1;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === ' ' && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
  }
  return best > 0 ? [text.slice(0, best), text.slice(best + 1)] : [text];
}

/** Each bar's sub-label lines: one line, unless it would sit closer than
 * MIN_SUB_GAP to a neighbour in its slot, then the wider of the pair wraps. */
function subLabelLines(subs: readonly string[], slot: number, family: string): string[][] {
  const lines = subs.map((sub) => [sub]);
  const width = (i: number) => Math.max(...lines[i]!.map((line) => subWidth(line, family)));
  for (let pass = 0; pass < 2; pass += 1) {
    for (let i = 0; i < subs.length - 1; i += 1) {
      if (slot - width(i) / 2 - width(i + 1) / 2 >= MIN_SUB_GAP) continue;
      const order = width(i) >= width(i + 1) ? [i, i + 1] : [i + 1, i];
      for (const j of order) {
        if (lines[j]!.length > 1) continue;
        const split = splitSub(subs[j]!);
        if (split.length > 1) {
          lines[j] = split;
          break;
        }
      }
    }
  }
  return lines;
}

function barFill(kind: EbitdaWaterfallBar['kind']): { fill: string; stroke: string; label: string } {
  switch (kind) {
    case 'total':
      return { fill: 'var(--ink-secondary)', stroke: 'none', label: 'var(--ink-primary)' };
    case 'up':
      return {
        fill: 'color-mix(in srgb, var(--state-online) 16%, transparent)',
        stroke: 'var(--state-online)',
        label: 'var(--state-online)',
      };
    case 'down':
      return {
        fill: 'color-mix(in srgb, var(--state-blocked) 14%, transparent)',
        stroke: 'var(--state-blocked)',
        label: 'var(--state-blocked)',
      };
    default:
      return { fill: 'var(--hairline-strong)', stroke: 'none', label: 'var(--ink-tertiary)' };
  }
}

/**
 * EBITDA bridge, Budget → actual, drawn as a waterfall (locked comp
 * `gm-landing.html` B, `.card` "EBITDA bridge"): totals stand on zero, each
 * step floats between the running totals; favourable steps read state-online,
 * adverse state-blocked. Values come from `ebitdaWaterfall()` (US$K).
 * Shared (polish.P7): the GM landing draws the closed month; Cost & revenue
 * passes its period switch.
 */
export function EbitdaWaterfall({ period = 'month' }: { period?: CostPeriod } = {}) {
  const { bars, max } = useMemo(() => ebitdaWaterfall(period), [period]);
  // Draw at the container's pixel width so the type never scales with the card
  // (the TrendChart rule): 11–12px labels stay 11–12px at 1280 and 1470.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [W, setW] = useState(640);
  const [family, setFamily] = useState('sans-serif');
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    setFamily(getComputedStyle(el).fontFamily || 'sans-serif');
    const measure = () => setW(Math.max(360, Math.round(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const top = Math.ceil((max * 1.06) / 100) * 100;
  const n = bars.length;
  const slot = (W - PAD.l - PAD.r) / n;
  const barW = slot * 0.58;
  const subLines = useMemo(() => subLabelLines(bars.map((bar) => bar.sub), slot, family), [bars, slot, family]);
  const axis = subLines.some((lines) => lines.length > 1) ? AXIS_TWO : AXIS_ONE;
  const base = PAD.t + PLOT_H;
  const H = base + axis;
  const y = (v: number) => PAD.t + PLOT_H * (1 - v / top);
  return (
    <div ref={wrapperRef} className="w-full">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        role="img"
        aria-label={`EBITDA bridge, Budget ${bars[0]?.valueLabel} to actual ${bars[n - 1]?.valueLabel} ${CURRENCY_GLYPH}K`}
        data-gm-waterfall
        className="block"
      >
        <line x1={PAD.l} x2={W - PAD.r} y1={base} y2={base} stroke="var(--hairline-strong)" strokeWidth={1} />
        {bars.map((bar, i) => {
          const x = PAD.l + i * slot + (slot - barW) / 2;
          const hi = Math.max(bar.from, bar.to);
          const lo = Math.min(bar.from, bar.to);
          const y1 = y(hi);
          const h = Math.max(2, y(lo) - y1);
          const tone = barFill(bar.kind);
          const next = bars[i + 1];
          return (
            <g key={bar.id} data-waterfall-bar={bar.id}>
              <rect
                x={x}
                y={bar.kind === 'flat' ? y(bar.to) - 1 : y1}
                width={barW}
                height={bar.kind === 'flat' ? 2 : h}
                rx={3}
                fill={tone.fill}
                stroke={tone.stroke}
                strokeWidth={1}
              />
              {next ? (
                <line
                  x1={x + barW}
                  x2={PAD.l + (i + 1) * slot + (slot - barW) / 2}
                  y1={y(bar.to)}
                  y2={y(bar.to)}
                  stroke="var(--ink-tertiary)"
                  strokeDasharray="2 2"
                  strokeWidth={1}
                />
              ) : null}
              <text
                x={x + barW / 2}
                y={(bar.kind === 'flat' ? y(bar.to) : y1) - 7}
                textAnchor="middle"
                fontSize={12}
                fontWeight={600}
                fill={tone.label}
                className="tabular-nums"
              >
                {bar.valueLabel}
              </text>
              <text x={x + barW / 2} y={base + 16} textAnchor="middle" fontSize={11} fontWeight={600} fill="var(--ink-primary)">
                {bar.name}
              </text>
              <text
                x={x + barW / 2}
                y={base + 29}
                textAnchor="middle"
                fontSize={SUB_FONT_PX}
                fill="var(--ink-tertiary)"
                data-waterfall-sub={bar.id}
              >
                {subLines[i]!.map((line, lineIndex) => (
                  <tspan key={line} x={x + barW / 2} dy={lineIndex === 0 ? 0 : 12}>
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
